import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LEARN_ROUTES } from '../config/learnRoutes';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useTranslation from '../hooks/useTranslation';
import * as webauthn from '../utils/webauthn';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithPasskey, isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  // Post-login target: honor a same-origin ?next= (e.g. /login?next=/enter for the seed
  // pilot), else the /learn dashboard (KAN-273 reworked flow: the account comes first,
  // the first experience is the dashboard's one to-do). Relative in-app paths only.
  const _next = new URLSearchParams(window.location.search).get('next');
  const postLoginTarget = _next && _next.startsWith('/') && !_next.startsWith('//') ? _next : LEARN_ROUTES.dashboard;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});

  useDocumentTitle('Sign In');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(postLoginTarget);
    }
  }, [isAuthenticated, authLoading, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t('auth.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth.errors.emailInvalid');
    }

    if (!formData.password) {
      newErrors.password = t('auth.errors.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    // Validate this field immediately on blur
    const newErrors = {};
    if (name === 'email') {
      if (!formData.email.trim()) newErrors.email = t('auth.errors.emailRequired');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('auth.errors.emailInvalid');
    }
    if (name === 'password' && !formData.password) newErrors.password = t('auth.errors.passwordRequired');
    setErrors(prev => ({ ...prev, ...newErrors }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setServerError('');

    const result = await login(formData.email, formData.password);

    setIsLoading(false);

    if (result.success) {
      navigate(postLoginTarget);
    } else {
      setServerError(result.error || 'Login failed. Please try again.');
    }
  };

  // Passkey sign-in (WebAuthn) — needs only the email field.
  const handlePasskeyLogin = async () => {
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors(prev => ({ ...prev, email: t('auth.errors.emailRequired') }));
      return;
    }

    setIsLoading(true);
    setServerError('');

    const result = await loginWithPasskey(formData.email);

    setIsLoading(false);

    if (result.success) {
      navigate(postLoginTarget);
    } else {
      setServerError(result.error || 'Passkey sign-in failed. Please try again.');
    }
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="flex-center" style={{ minHeight: '400px' }}>
          <div className="spinner spinner-lg"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-sm">
        <div style={styles.pageHeader}>
          <h1>{t('auth.loginTitle')}</h1>
          <p>{t('auth.loginSub')}</p>
        </div>

        <div className="card" style={styles.formCard}>
          {serverError && (
            <div style={styles.errorAlert} role="alert" aria-live="assertive">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email">
                {t('auth.email')} <span className="required" aria-hidden="true">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLoading}
                placeholder={t('auth.emailPlaceholder')}
                autoComplete="email"
                autoFocus
                aria-required="true"
                aria-describedby={errors.email ? 'email-error' : undefined}
                aria-invalid={!!errors.email}
              />
              {errors.email && <div id="email-error" className="form-error" role="alert">{errors.email}</div>}
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password">
                {t('auth.password')} <span className="required" aria-hidden="true">*</span>
              </label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  style={styles.passwordInput}
                  aria-required="true"
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  aria-invalid={!!errors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={styles.showPasswordBtn}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && <div id="password-error" className="form-error" role="alert">{errors.password}</div>}
            </div>

            <div style={{ textAlign: 'right', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
              <a href="/forgot-password" style={styles.link}>Forgot password?</a>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`btn btn-primary btn-lg ${isLoading ? 'btn-loading' : ''}`}
              disabled={isLoading}
              style={styles.submitButton}
            >
              {isLoading ? t('auth.loggingIn') : t('auth.loginBtn')}
            </button>
          </form>

          {/* Passkey sign-in (only when the browser supports WebAuthn) */}
          {webauthn.isSupported() && (
            <button
              type="button"
              className="btn btn-secondary btn-lg"
              onClick={handlePasskeyLogin}
              disabled={isLoading}
              style={styles.passkeyButton}
            >
              🔑 Sign in with passkey
            </button>
          )}

          {/* Register Link */}
          <div style={styles.footer}>
            <p>
              {t('auth.noAccount')}{' '}
              <a href={`/register${window.location.search}`} style={styles.link}>
                {t('auth.registerLink')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const styles = {
  pageHeader: {
    textAlign: 'center',
    marginBottom: 'var(--spacing-2xl)',
  },
  formCard: {
    maxWidth: '500px',
    margin: '0 auto',
  },
  errorAlert: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid var(--error)',
    borderRadius: 'var(--border-radius)',
    padding: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-lg)',
    color: 'var(--error)',
    fontSize: '0.875rem',
  },
  submitButton: {
    width: '100%',
    marginTop: 'var(--spacing-lg)',
  },
  passkeyButton: {
    width: '100%',
    marginTop: 'var(--spacing-md)',
  },
  footer: {
    textAlign: 'center',
    marginTop: 'var(--spacing-lg)',
    paddingTop: 'var(--spacing-lg)',
    borderTop: '1px solid var(--gray-200)',
  },
  link: {
    color: 'var(--primary)',
    textDecoration: 'none',
    fontWeight: '600',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    flex: 1,
    paddingRight: '2.75rem',
  },
  showPasswordBtn: {
    position: 'absolute',
    right: '0.75rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0',
    lineHeight: '1',
    color: 'var(--gray-500)',
  },
};

export default LoginPage;
